import {Box} from '@mui/material';

import React, {useEffect, useState} from 'react';
import AurDepartmentForm from './AurDepartmentForm';
import AurDrawerHeader from '../common/aur-drawer/AurDrawerHeader';
import {useDispatch, useSelector} from 'react-redux';
import {
  setDepartmentAccordionCollapsed,
  setDrawerAccordionDisable,
  triggerResize,
  setClientDetailsFormState,
  setCreateMode,
} from '../../redux/slices/clientDetailsSlice';
import {RootState} from '../../redux/store';
import {useToast} from '../../contexts/toastContext/ToastContext';
import {
  branchValidationText,
  descriptionValidationText,
  isValidDepartmentName,
  nameValidationText,
} from '../../utils/clientDetailsUtils';
import useValidation from '../../hooks/client-management/useDepartmentValidation';
import {
  Branch,
  IDepartmentForm,
  IFormField,
  IValidationErrors,
} from '../../types/client-management/departmentTypes';
import {useModal} from '../../contexts/modalContext/ModalContext';
import {t} from 'i18next';
import AurIcon, {IconNames, IconColors} from '../common/aur-icon/AurIcon';
import AurButton from '../common/aur-button/AurButton';
import styles from '../../styles/components/clientdetails/clientDetailsStyle.module.scss';
import {addNewClientDepartment} from '../../api/ClientManagementService';
import { MAX_CHAR, MAX_CHAR_LIMIT } from '../../constants/constants';

type ClientDepartmentApiDto = {
  clientDepartmentId: string;
  name: string;
  description: string;
  clientBranchId: string;
  clientBranchName: string;
  createdBy?: string;
  editedBy?: string;
  createdOn?: string;
  editedOn?: string;
};

type ChildComponentProps = {
  onDepartmentCreated?: (department: ClientDepartmentApiDto) => void;
  onUpdate: () => void;
};

const AurDrawerClientAddDepartment: React.FC<ChildComponentProps> = ({
  onDepartmentCreated,
  onUpdate,
}) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<IDepartmentForm>(
    {} as IDepartmentForm
  );
  const [formVisible, setFormVisible] = useState<boolean>(false);
  const [validationActive, setValidationActive] = useState<boolean>(false);

  const [btnDisable, setBtnDisable] = useState<boolean>(true);
  const branchList = useSelector(
    (state: RootState) => state.department.branchList
  );
  const onEditMode = useSelector(
    (state: RootState) => state.department.onEditMode
  );
  const {showToast} = useToast();
  const formState = useSelector(
    (state: RootState) => state.department.isFormStateChanged
  );
  const clientId = useSelector(
    (state: RootState) => state.clientList.selectedClientId
  );
  const {showModal} = useModal();

  const {validationState: nameValidation, setValidationError: setNameError} =
    useValidation(
      nameValidationText,
      t('common.validationText.specialCharacterWarning')
    );

  const {
    validationState: descriptionValidation,
    setValidationError: setDescriptionError,
  } = useValidation(
    descriptionValidationText,
    t('common.validationText.maxLengthWarning')
  );
  const {
    validationState: branchValidation,
    setValidationError: setBranchError,
  } = useValidation(branchValidationText, '');

  const validate = (data: IDepartmentForm, key?: keyof IDepartmentForm) => {
    let hasError = false;
    if (!key || key == 'name') {
      if (data.name.length > MAX_CHAR_LIMIT) {
        hasError = true;
        setNameError('maxLength');
      } else if (!isValidDepartmentName(data.name)) {
        hasError = true;
        setNameError('specialChar');
      } else {
        setNameError('');
      }
    }
    if (!key || key == 'description') {
      if (data.description.length > MAX_CHAR) {
        hasError = true;
        setDescriptionError('maxLength');
      } else {
        setDescriptionError('');
      }
    }
    if (!key || key == 'branch') {
      if (!data.branch?.clientBranchId) {
        hasError = true;
        setBranchError('required');
      } else {
        setBranchError('');
      }
    }
    return hasError;
  };

  const AddNewButtonClickHandler = () => {
    setFormData({
      departmentId: '',
      name: '',
      description: '',
      branch: {} as Branch,
    });
    setNameError('');
    setDescriptionError('');
    setBranchError('');
    dispatch(setDepartmentAccordionCollapsed(false));

    dispatch(setDrawerAccordionDisable(true));
    setBtnDisable(true);
    setFormVisible(true);
    dispatch(setCreateMode(true));
    dispatch(triggerResize());
  };

  const handleValidationErrors = (validationFailures?: IValidationErrors) => {
    if (validationFailures) {
      showModal({
        title: t('clientDetails.modal.departmentDuplicate.title'),
        description: t('clientDetails.modal.departmentDuplicate.description'),
        labelOkButton: t('clientDetails.modal.departmentDuplicate.buttonLabel'),
        buttonStyle: 'single',
      });
    }
  };

  const makeDepartmentPostRequest = async () => {
    try {
      setBtnDisable(true);
      const response = await addNewClientDepartment({
        clientId,
        branchId: formData.branch.clientBranchId,
        name: formData.name,
        description: formData.description,
      });
      if (
        !(
          response.data.data?.hasException ||
          response.data.data?.hasValidationError
        )
      ) {
        showToast(
          t('clientDetails.toast.addDepartment.successToastText'),
          'success'
        );
        const createdDepartment = response.data?.data as
          | ClientDepartmentApiDto
          | undefined;
        if (createdDepartment) {
          onDepartmentCreated?.(createdDepartment);
        }
        clearFormState();
        setTimeout(() => {
          dispatch(triggerResize());
        }, 10);
      }
    } catch (error: any) {
      setBtnDisable(false);
      if (error.response.data.data?.hasValidationError) {
        handleValidationErrors(error.response.data.data?.validationFailures);
      } else {
        showToast('clientDetails.toast.addDepartment.failedToastText', 'error');
      }
    }
  };

  const clearFormState = () => {
    setBtnDisable(false);
    setFormData((previousData) => {
      return {...previousData, name: '', description: ''};
    });
    setValidationActive(false);
    setFormVisible(false);
    dispatch(setDrawerAccordionDisable(false));
    dispatch(setClientDetailsFormState(false));
    dispatch(setCreateMode(false));
    dispatch(triggerResize());
  };

  const saveButtonClickHandler = async () => {
    setValidationActive(true);
    if (!validate(formData)) {
      await makeDepartmentPostRequest();
      onUpdate();
    }
  };

  const noSaveButtonClickHandler = () => {
    if (formState) {
      showModal({
        title: t('common.clientDetailsUnsavedChangePopup.title'),
        description: t('common.clientDetailsUnsavedChangePopup.description'),
        labelCancelButton: t(
          'common.clientDetailsUnsavedChangePopup.cancelButtonLabel'
        ),
        labelOkButton: t(
          'common.clientDetailsUnsavedChangePopup.okButtonLabel'
        ),
        onOk: clearFormState,
      });
      return;
    }
    clearFormState();
  };

  const setDepartmentFormData = (fieldData: IFormField) => {
    setFormData((previousState) => {
      const newState = {...previousState, [fieldData.name]: fieldData.value};
      validate(newState, fieldData.name);

      if (
        newState.name !== '' ||
        newState.description !== '' ||
        Object.keys(newState.branch).length !== 0
      ) {
        dispatch(setClientDetailsFormState(true));
      } else {
        dispatch(setClientDetailsFormState(false));
      }

      return {...newState};
    });
  };

  useEffect(() => {
    if (
      descriptionValidation.isError ||
      nameValidation.isError ||
      branchValidation.isError
    ) {
      setBtnDisable(true);
    }
  }, [
    descriptionValidation,
    descriptionValidation,
    branchValidation,
    nameValidation,
  ]);

  const manageDeptHeaderButtons = [
    <AurButton
      key={0}
      disabled={onEditMode}
      type="continue"
      title={t('buttons.addNew')}
      startIcon={
        <AurIcon
          icon={IconNames.Add}
          color={IconColors.Custom}
          customClassName="icon-white"
        />
      }
      label={t('buttons.addNew')}
      size="Large"
      onClick={AddNewButtonClickHandler}
    />,
  ];

  const manageDeptHeader = (
    <AurDrawerHeader
      title={t('clientDetails.department.manageDeparments')}
      buttons={manageDeptHeaderButtons}
    />
  );

  const addDeptHeaderButtons = [
    <AurButton
      key={0}
      type="exit"
      title={t('common.unSavedChangesPopup.cancelBtn')}
      label={t('common.unSavedChangesPopup.cancelBtn')}
      size="Medium"
      onClick={noSaveButtonClickHandler}
    />,
    <AurButton
      key={1}
      disabled={btnDisable}
      type="continue"
      title={t('common.unSavedChangesPopup.saveBtn')}
      label={t('common.unSavedChangesPopup.saveBtn')}
      size="Medium"
      onClick={saveButtonClickHandler}
    />,
  ];

  const addDeptHeader = (
    <AurDrawerHeader
      title={t('clientDetailsPage.projectDrawer.drawerHeaderTitle')}
      titleCssClass={`${styles.addDepartmentHeaderText}`}
      buttons={addDeptHeaderButtons}
    />
  );

  const addDeptForm = (
    <Box>
      {addDeptHeader}
      <AurDepartmentForm
        departmentFormData={formData}
        branches={branchList}
        isValidationAcive={validationActive}
        setDepartmentFormData={setDepartmentFormData}
        SaveButtonClickHandler={saveButtonClickHandler}
        noSaveButtonClickHandler={noSaveButtonClickHandler}
        nameValidation={nameValidation}
        descriptionValidation={descriptionValidation}
        branchValidation={branchValidation}
        setBtnDisable={setBtnDisable}
        btnDisable={btnDisable}
      />
    </Box>
  );

  return <Box>{formVisible ? addDeptForm : manageDeptHeader}</Box>;
};

export default AurDrawerClientAddDepartment;
